<template>
  <li
    v-if="showGoal"
    :class="goalCardClass"
    style="cursor:pointer;"
    @click="onClickCard">
    <div class="goal-card__head">
      <div class="goal-card__unit d-flex justify-content-between align-items-center mb-1">
        <div class="goal-card__results">
          <span class="goal-card__finished mr-1">{{ goal.totalActivityCount }}</span>/{{ goal.goalNumber }}
        </div>
        <div class="goal-card__right d-flex align-items-center">
          <div
            v-if="hasConditionsOrBodyPain"
            class="goal-card__condition pr-1 d-flex align-items-center">
            <img
              class="goal-card__condition-icon"
              src="@/assets/images/icon_condition.svg">
          </div>
          <div
            v-if="hasRepeatGroup"
            class="goal-card__repeat pr-1 d-flex align-items-center">
            <img
              class="goal-card__repeat-icon"
              src="@/assets/images/icon_repeat_active.svg">
          </div>
          <div
            class="goal-card__deadline"
            :class="isTimeLimitSoon">
            <span class="goal-card__deadline-icon material-icons">
              event_available
            </span>
            {{ goal.endDate | prettyDate }}
          </div>
        </div>
      </div>

      <h2
        class="goal-card__heading mb-1"
        :class="h2Class">
        <span
          v-if="goal.isLocked"
          class="material-icons goal-lock-icon">
          lock
        </span>
        <span>
          {{ `${repeatYearMonth}${goal.name}` }}
        </span>
      </h2>

      <div class="d-flex justify-content-between align-items-center mb-3">
        <div class="goal-card__bar bar">
          <b-progress
            class="mb-3"
            :max="goal.goalNumber">
            <b-progress-bar
              variant="progress"
              :value="previousActivityCount" />
            <b-progress-bar
              variant="today"
              :value="selectedDateActivityCount" />
          </b-progress>
        </div>
        <div class="goal-card__persent">
          <p>{{ achievementRate }}<span class="goal-card__small">%</span></p>
        </div>
      </div>
      <transition name="list-menu">
        <div
          v-show="isShowDetail"
          style="cursor: auto;"
          @click.stop>
          <div
            v-if="isLoginUser && !isPeriodOver"
            class="goal-detail__edit mb-3">
            <span
              style="cursor: pointer;"
              class="goal-detail__icon material-icons">arrow_right</span>
            <span
              style="cursor: pointer;"
              @click="onClickEditGoalBtn">ノートを編集する</span>

            <div
              v-if="isLoginUser && !isPeriodOver"
              class="text-right mt-3">
              <button
                class="goal__add-btn btn--circle-l btn-secondary"
                @click="onClickAddActivityBtn">
                <span class="goal__add-icon material-icons">
                  add
                </span>
              </button>
            </div>
          </div>
          <div v-if="isConditionButtonActive">
            <ConditionPanel
              v-for="activity in activitiesWithConditions"
              :key="activity.id"
              :activity="activity"
              class="condition-panel--condition-button-active">
              <template #header>
                <div class="condition-panel__work-at d-flex align-items-center">
                  <div class="date">
                    {{ activity.workAt | prettyMdWithJpWeekDay }}
                  </div>
                  <div class="time">
                    {{ activity.workAt | prettyHHmm }}
                  </div>
                </div>
              </template>
            </ConditionPanel>
          </div>
          <div v-else>
            <goal-card-activity-panel
              v-for="activity in goal.activities"
              :key="activity.id"
              style="cursor: auto;"
              :member-id="memberId"
              :login-user-id="loginUserId"
              :is-period-over="isPeriodOver"
              :goal-id="goal.id"
              :is-show-detail="activity.isShowDetail"
              :attachment-option="attachmentOption"
              :activity="activity"
              :condition-setting="goal.conditionSetting"
              :body-pain-setting="goal.bodyPainSetting"

              @createComment="createComment"
              @deletedComment="deletedComment"
              @showActivityDetail="showActivityDetail"
              @like="onLike"
              @dislike="onDislike"
              @like-count="onLikeCount" />
          </div>
        </div>
      </transition>
    </div>
  </li>
</template>

<script>
import GoalCardActivityPanel from '@/components/parts/GoalCardActivityPanel.vue'
import ConditionPanel from '@/components/parts/ConditionPanel.vue'
import { mapState } from 'vuex'
import { format, endOfDay, addDays } from 'date-fns'

export default {
  components: {
    GoalCardActivityPanel,
    ConditionPanel
  },
  props: {
    goal: {
      type: Object,
      required: true
    },
    loginUserId: {
      type: String,
      required: true
    },
    memberId: {
      type: String,
      required: true
    },
    selectedDate: {
      type: String,
      required: true
    },
    isShowDetail: {
      type: Boolean,
      required: true
    },
    isPeriodOver: {
      type: Boolean,
      required: true
    },
    attachmentOption: {
      type: Boolean,
      required: true
    },
    lockGoalOption: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    ...mapState(['isConditionButtonActive']),
    // 鍵付きオプションがoffの時に鍵付き目標は表示しない
    showGoal() {
      if (!this.goal) return false
      if (!this.lockGoalOption && this.goal.isLocked) return false
      return true
    },
    goalCardClass() {
      if (this.goal.pin) {
        return 'goal-card--pinned'
      }
      return ''
    },
    h2Class(){
      if (this.goal.totalActivityCount >= this.goal.goalNumber) {
        return 'goal-card__heading--finished'
      }
      return ''
    },
    selectedDateActivityCount(){
      const selectedDateActibities = this.goal.activities.filter(v=> format(new Date(v.workAt), 'yyyy-MM-dd') === this.selectedDate)
      if (selectedDateActibities.length > 0){
        return selectedDateActibities.reduce((a, c)=> {return a + c.resultCount}, 0)
      } else {
        return 0
      }
    },
    achievementRate() {
      const num = this.goal.totalActivityCount / this.goal.goalNumber * 100
      return Math.round(num)
    },
    previousActivityCount() {
      return this.goal.totalActivityCount - this.selectedDateActivityCount
    },
    isLoginUser() {
      return this.loginUserId === this.memberId
    },
    isTimeLimitSoon(){
      // 繰り返し目標の場合は未完了の目標でも赤字にしない
      if (this.hasRepeatGroup) {
        return ''
      }

      //1週間前に未完了の目標がある場合は赤字にする。
      const today = addDays(endOfDay(new Date()), 7)
      const endDate = endOfDay(new Date(this.goal.endDate))
      if (today >= endDate && this.goal.totalActivityCount < this.goal.goalNumber){
        return 'goal-card__deadline--past'
      }
      return ''
    },
    hasRepeatGroup() {
      return this.goal.repeatGroup !== null
    },
    hasConditionsOrBodyPain() {
      return this.goal.conditionSetting?.items.length > 0 || this.goal.bodyPainSetting?.isRecordBodyPain
    },
    repeatYearMonth() {
      if (!this.hasRepeatGroup) {
        return ''
      }
      return format(new Date(this.goal.startDate), 'M月 ')
    },
    activitiesWithConditions() {
      return this.goal.activities.filter(item => item.bodyPain !== null || item.condition !== null)
    }
  },
  methods: {
    onClickCard() {
      this.$emit('showGoalDetail', this.goal.id)
    },
    onClickEditGoalBtn() {
      this.$router.push(`${this.memberId}/goal_edit/${this.goal.id}`)
    },
    onClickAddActivityBtn() {
      this.$router.push(`${this.memberId}/goal/${this.goal.id}/activity_result_add`)
    },
    deletedComment(activityId, commentId){
      this.$emit('deletedComment', this.goal.id, activityId, commentId)
    },
    createComment(activityId, text, file){
      this.$emit('createComment', this.goal.id, activityId, text, file)
    },
    showActivityDetail(goalId, activityId){
      this.$emit('showActivityDetail', goalId, activityId)
    },
    onLike(activityId) {
      this.$emit('like', activityId)
    },
    onDislike(activityId) {
      this.$emit('dislike', activityId)
    },
    onLikeCount(activityId) {
      this.$emit('like-count', activityId)
    },
  }
}
</script>

<style lang="scss" scoped>
.goal{
  position: relative;
  &__head{
    background-color: $white;
    border-bottom: solid 1px $grey11;
  }
  &__add{
    @media screen and (min-width:992px) {
      position: fixed;
      right: 16px;
      bottom: 80px;
    }
  }
  &__add-btn{
    box-shadow: 1px 2px 6px $grey05;
    background-color: $white;
    color: $l-blue;
  }
  &__add-btn--active{
    color: $grey05;
  }
  &__add-icon{//ボタン
    font-size: 30px;
    line-height: 54px;
    width: 30px;
    &--s{
      font-size: 21px;
      line-height: 34px;
    }
  }
}

.goal-head{
  max-width: 500px;
  margin: auto;
  @media screen and (min-width:768px) {
    width: 100%;
    max-width: 100%;
  }
  &__icon{
    &--list{
      color: $grey12;
    }
    &--activity{
      width: 24px;
      height: 24px;
    }
  }
  &__img{
    width: 22px;
    height: 22px;
    object-fit: cover;
  }
  &__name{
    font-size: $fs-nm;
    font-weight: bold;
  }
}

.goal-card{
  background-color: $white;
  border-radius: 8px;
  position: relative;
  &--pinned{//固定されたカード
    &:after{
      content: "";
      background-image: url(~@/assets/images/icon_pin.svg);
      background-repeat: no-repeat;
      width: 20px;
      height: 20px;
      display: block;
      position: absolute;
      right: 0;
      top: -10px;
    }
  }
  &__heading{
    display: flex;
    align-items: center;
    font-size: $fs-nm;
    font-weight: bold;
    @media screen and (max-width:320px) {
      font-size: $fs-m;
    }
    &--finished{
      position: relative;
      &:after{
        content: "";
        background-image: url(~@/assets/images/icon_flag-b.svg);
        background-repeat: no-repeat;
        width: 20px;
        height: 20px;
        display: block;
        position: absolute;
        right: 6%;
        top: 8px;
      }
    }
  }
  &__unit{
    color: $grey13;
    vertical-align: middle;
  }
  &__results{
    color: $grey13;
    font-size: $fs-m;
    font-weight: bold;
  }
  &__finished{
    color: $primary;
  }
  &__deadline{
    font-size: $fs-s;
    color: $grey09;
    &--past{
      color: $pink;
    }
  }
  &__deadline-icon{
    font-size: $fs-m;
    display: inline-block;
    vertical-align: middle;
  }
  &__repeat-icon{
    width: 14px;
    height: 14px;
    display: inline-block;
    vertical-align: middle;
  }
  &__condition-icon{
    width: 14px;
    height: 14px;
    display: inline-block;
    vertical-align: middle;
    filter: invert(28%) sepia(0%) saturate(1%) hue-rotate(340deg) brightness(92%) contrast(86%);
  }
  &__bar{
    position: relative;
    width: 90%;
    height: 20px;
    background: $grey14;
    border-radius: 10px;
  }

  .progress{
    border-radius: 10px;
    height: 20px;
    background: $grey14;
    transition: width 3.3s ease-in-out;
  }
  .progress-bar{
    background: linear-gradient(#6DC2F1, $l-blue);
    &.bg-today{
      background: $blue;
    }
  }
  &__persent{
    font-size: $fs-m;
    width: 8%;
    font-weight: bold;
    text-align: right;
  }
  &__small{
    font-size: $fs-xs;
    font-weight: normal;
  }
}

.goal-detail{
  &__edit{
    color: $grey01;
    text-decoration: none;
    display: block;
    text-align: right;
    font-weight: bold;
    @media screen and (max-width:320px) {
      font-size: $fs-m;
    }
  }
  &__icon{
    display: inline-block;
    vertical-align: middle;
  }
  &__item{
    border: solid 1px $grey10;
    border-radius: 8px;
  }
  &__heading{
    font-size: $fs-nm;
    font-weight: bold;
    position: relative;
    padding-left: 16px;
    @media screen and (max-width:320px) {
      font-size: $fs-m;
    }
    &:before{
      content: "";
      position: absolute;
      background: $grey16;
      top: 4px;
      left: 0;
      height: 12px;
      width: 12px;
      border-radius: 50%;
    }
    &--marked{
      &:before{
        background: $pink;
      }
    }
  }
  &__box{
    text-align: right;
  }
  &__count{
    font-size: $fs-m;
    color: $l-blue;
    font-weight: bold;
  }
  &__date{
    font-size: $fs-s;
  }
  &__body{
    border-top: solid 1px $grey10;
  }
}

.list-menu-enter-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.list-menu-leave-active {
  transition: all 0.1s;
}
.list-menu-enter, .list-menu-leave-to {
  opacity: 0;
  transform: translateY(-15px);
}
.condition-panel {
  &--condition-button-active {
    border: solid 1px $grey10;
    padding: 8px 12px 0;
    margin-bottom: 8px;
  }

  &__work-at {
    margin-bottom: 5px;
    .date {
      font-size: $fs-s;
      margin-right: 10px;
    }

    .time {
      font-size: $fs-xs;
    }
  }
}

</style>